<!-- eslint-disable vue/no-mutating-props -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import JiraAPI from 'dashboard/api/integrations/jira';

import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import FileUpload from 'vue-upload-component';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';

export default {
  components: { FileUpload, AttachmentPreview },
  mixins: [fileUploadMixin],
  props: {
    inbox: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isCreating: false,
      isFetchingIssueTypes: false,
      errorWhenFetchingIssueTypes: false,

      issueTypes: [],
      selectedIssueType: null,
      selectedPriority: null,
      summary: '',
      description: '',

      attachedFiles: [],
    };
  },
  computed: {
    ...mapGetters({
      conversation: 'getSelectedChat',
      accountId: 'getCurrentAccountId',
      globalConfig: 'globalConfig/get',
    }),
    account() {
      return this.$store.getters['accounts/getAccount'](this.accountId);
    },
    issueTypeId() {
      return this.selectedIssueType?.id;
    },
    priorityId() {
      return this.selectedPriority?.id;
    },
    buttonDisabled() {
      return !(!!this.summary && !!this.issueTypeId && !!this.priorityId);
    },
    allowedFileTypes() {
      return ALLOWED_FILE_TYPES;
    },
  },
  watch: {
    selectedIssueType() {
      this.selectedPriority = null; // Reset priority if issue type is changed
    },
  },
  mounted() {
    this.fetchIssueTypes();
    this.setInitialData();
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    async fetchIssueTypes() {
      this.isFetchingIssueTypes = true;
      try {
        const { data } = await JiraAPI.fetchIssueTypes();
        if (!data) throw new Error('Jira API error');
        this.issueTypes = data;
      } catch (e) {
        this.errorWhenFetchingIssueTypes = true;
        useAlert(
          this.$t(
            'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.API.FETCH_ISSUE_TYPES.ERROR_MESSAGE'
          )
        );
      }
      this.isFetchingIssueTypes = false;
    },
    async createJiraIssue() {
      this.isCreating = true;
      if (this.attachedFiles.length > 3) {
        useAlert(
          this.$t(
            'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.MODAL.ATTACHMENT_LIMIT'
          )
        );
        this.isCreating = false;
        return;
      }
      try {
        const payload = {
          summary: this.summary,
          description: this.description,
          selectedIssueTypeId: this.selectedIssueType.id,
          selectedPriorityId: this.selectedPriority.id,
          attachments: this.attachedFiles.map(e => e.resource.file),
        };
        await this.$store.dispatch('newJiraIssue', {
          conversationId: this.conversation.id,
          payload,
        });
        useAlert(
          this.$t(
            'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.API.CREATE_TICKET.SUCCESS_MESSAGE'
          )
        );
        this.onClose();
      } catch (e) {
        useAlert(
          this.$t(
            'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.API.CREATE_TICKET.ERROR_MESSAGE'
          )
        );
      }
      this.isCreating = false;
    },
    setInitialData() {
      this.description = this.account.jira_integration?.template_message || '';
    },
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          resource: blob || file,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
      };
    },
  },
};
</script>

<template>
  <div>
    <woot-modal-header
      :header-title="$t('INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.MODAL.HEADER')"
      style="margin-bottom: 1rem"
    />
    <div
      class="flex flex-col row justify-center content-box w-[90%]"
      style="margin: 0 auto"
    >
      <woot-loading-state
        v-if="isFetchingIssueTypes"
        class="text-xs"
        :message="
          $t(
            'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.API.FETCH_ISSUE_TYPES.FETCHING_MESSAGE'
          )
        "
      />
      <span
        v-else-if="errorWhenFetchingIssueTypes"
        class="text-sm text-red-600 text-center"
      >
        {{
          $t(
            'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.API.FETCH_ISSUE_TYPES.ERROR_MESSAGE'
          )
        }}
      </span>
      <div v-else class="main-container">
        <!-- START ISSUE TYPE SELECTOR SECTION -->
        <div class="w-full">
          <div>
            {{ $t('INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.MODAL.ISSUE_TYPE') }}
            <multiselect
              v-model="selectedIssueType"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label=""
              select-label=""
              deselect-label=""
              :max-height="160"
              close-on-select
              :options="[...issueTypes]"
            >
              <!-- Whats is displayed when it's selected -->
              <template #singleLabel="{ option }">
                <div class="flex gap-2 align-center">
                  <img
                    class="w-5 h-5"
                    :src="option.icon_url"
                    :alt="option.name"
                  />
                  <span>{{ option.name }}</span>
                </div>
              </template>
              <!-- Whats is displayed when selecting (list is open) -->
              <template #option="{ option }">
                <div class="flex gap-2 align-center">
                  <img
                    class="w-5 h-5"
                    :src="option.icon_url"
                    :alt="option.name"
                  />
                  <span>{{ option.name }}</span>
                </div>
              </template>
            </multiselect>
          </div>
        </div>
        <!-- END ISSUE TYPE SELECTOR SECTION -->
        <!-- START ISSUE TYPE SELECTOR SECTION -->
        <div v-if="!!selectedIssueType" class="w-full">
          {{ $t('INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.MODAL.PRIORITY') }}
          <multiselect
            v-model="selectedPriority"
            track-by="id"
            label="name"
            :placeholder="$t('FORMS.MULTISELECT.SELECT')"
            selected-label=""
            select-label=""
            deselect-label=""
            :max-height="160"
            close-on-select
            :options="[...selectedIssueType.allowed_priorities]"
          >
            <!-- Whats is displayed when it's selected -->
            <template #singleLabel="{ option }">
              <div class="flex gap-2 align-center">
                <img class="w-5 h-5" :src="option.iconUrl" :alt="option.name" />
                <span>{{ option.name }}</span>
              </div>
            </template>
            <!-- Whats is displayed when selecting (list is open) -->
            <template #option="{ option }">
              <div class="flex gap-2 align-center">
                <img class="w-5 h-5" :src="option.iconUrl" :alt="option.name" />
                <span>{{ option.name }}</span>
              </div>
            </template>
          </multiselect>
        </div>
        <!-- END ISSUE TYPE SELECTOR SECTION -->
        <!-- START SUMARY SECTION -->
        <div class="w-full">
          <div>
            {{ $t('INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.MODAL.SUMMARY') }}
            <input
              v-model.trim="summary"
              type="text"
              :placeholder="
                $t(
                  'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.MODAL.SUMMARY_PLACEHOLDER'
                )
              "
            />
          </div>
        </div>
        <!-- END SUMMARY SECTION -->
        <!-- START SUMARY SECTION -->
        <div class="w-full">
          <div>
            {{
              $t('INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.MODAL.DESCRIPTION')
            }}
            <textarea
              v-model="description"
              class="min-h-[8rem] text-sm"
              rows="4"
              :placeholder="
                $t(
                  'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.MODAL.DESCRIPTION_PLACEHOLDER'
                )
              "
            />
          </div>
        </div>
        <!-- END SUMMARY SECTION -->
        <!-- START ATTACH FILES SECTION -->
        <div class="w-full">
          <div>
            {{ $t('INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.MODAL.ATTACHMENT') }}
          </div>
          <FileUpload
            ref="upload"
            input-id="jiraIssueAttachment"
            :size="4096 * 4096"
            :accept="allowedFileTypes"
            multiple
            drop
            :drop-directory="false"
            :data="{
              direct_upload_url: '/rails/active_storage/direct_uploads',
              direct_upload: true,
            }"
            @input-file="onFileUpload"
          >
            <woot-button
              v-if="true"
              icon="attach"
              emoji="📎"
              color-scheme="secondary"
            >
              {{
                $t(
                  'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.MODAL.ATTACHMENT_BUTTON'
                )
              }}
            </woot-button>
          </FileUpload>
          <div v-if="attachedFiles.length">
            <AttachmentPreview
              class="flex-col mt-4"
              :attachments="attachedFiles"
              :remove-attachment="removeAttachment"
            />
          </div>
        </div>
        <!-- END ATTACH FILES SECTION -->
      </div>

      <div class="flex justify-center pt-2">
        <woot-button
          class-names="resolve"
          color-scheme="success"
          :is-loading="isCreating"
          style="margin-bottom: 2rem"
          :disabled="buttonDisabled"
          @click="createJiraIssue"
        >
          {{ $t('INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.BUTTON') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>
